import React from "react";
import { FiPackage, FiPlus, FiCheck } from "react-icons/fi";
import { PackageConfig, Addon } from "@/types";
import { formatCurrency } from "@/utils/format";
import Badge from "@/components/ui/Badge";
import Card from "@/components/ui/Card";

interface VisualPackageBuilderProps {
  selectedPackage: PackageConfig | null;
  selectedAddons: Addon[];
  includedAddons: Addon[];
  subtotal: number;
  total: number;
  className?: string;
}

const VisualPackageBuilder: React.FC<VisualPackageBuilderProps> = ({
  selectedPackage,
  selectedAddons,
  includedAddons,
  subtotal,
  total,
  className = "",
}) => {
  if (!selectedPackage) return null;

  return (
    <Card className={`sticky top-4 ${className}`}>
      <div className="p-4 sm:p-6 space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-gray-200 dark:border-gray-700">
          <FiPackage className="text-primary-600" size={24} />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Your Package</h3>
        </div>

        {/* Selected Package */}
        <div className="space-y-2">
          <div className="flex justify-between items-start">
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white">
                {selectedPackage.name}
              </h4>
              {selectedPackage.badge && (
                <Badge
                  variant={
                    selectedPackage.badge === "popular" || selectedPackage.badge === "best-value"
                      ? "primary"
                      : selectedPackage.badge === "recommended"
                        ? "success"
                        : "warning"
                  }
                  size="sm"
                  className="mt-1"
                >
                  {selectedPackage.badgeText || selectedPackage.badge.toUpperCase()}
                </Badge>
              )}
            </div>
            <div className="text-right">
              {selectedPackage.originalPrice &&
                selectedPackage.originalPrice > selectedPackage.price && (
                  <div className="text-sm text-gray-500 line-through">
                    {formatCurrency(selectedPackage.originalPrice)}
                  </div>
                )}
              <div className="font-bold text-gray-900 dark:text-white">
                {formatCurrency(selectedPackage.price)}
              </div>
            </div>
          </div>
        </div>

        {/* Included Addons */}
        {includedAddons.length > 0 && (
          <div className="space-y-2">
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Included Features:
            </h5>
            <div className="space-y-1">
              {includedAddons.map((addon) => (
                <div
                  key={addon.id}
                  className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
                >
                  <FiCheck className="text-green-600 flex-shrink-0" size={16} />
                  <span>{addon.title}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Selected Optional Addons */}
        {selectedAddons.length > 0 && (
          <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Additional Add-ons:
            </h5>
            <div className="space-y-2">
              {selectedAddons.map((addon) => (
                <div key={addon.id} className="flex items-center justify-between gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <FiPlus className="text-primary-600 flex-shrink-0" size={16} />
                    <span className="text-gray-900 dark:text-white">{addon.title}</span>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">
                    +{formatCurrency(addon.price)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Price Summary */}
        <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
            <span>Subtotal</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          {selectedPackage.discount && selectedPackage.discount > 0 && (
            <div className="flex justify-between text-sm text-green-600">
              <span>Discount ({selectedPackage.discount}%)</span>
              <span>-{formatCurrency(subtotal * (selectedPackage.discount / 100))}</span>
            </div>
          )}
          <div className="flex justify-between text-lg font-bold text-gray-900 dark:text-white pt-2 border-t border-gray-200 dark:border-gray-700">
            <span>Total</span>
            <span className="text-primary-600">{formatCurrency(total)}</span>
          </div>
        </div>

        {/* Delivery Estimate */}
        {selectedPackage.validUntil && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
            <p className="text-xs text-yellow-800 dark:text-yellow-300 font-medium">
              ⏰ Limited time offer! Valid until{" "}
              {new Date(selectedPackage.validUntil).toLocaleDateString()}
            </p>
          </div>
        )}

        {selectedPackage.seasonal && selectedPackage.seasonalTheme && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
            <p className="text-xs text-blue-800 dark:text-blue-300 font-medium">
              🎉 {selectedPackage.seasonalTheme}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default VisualPackageBuilder;
